import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

import {
  ApolloError,
  MutationHookOptions,
  MutationTuple,
} from '@apollo/client';

import { alertApiRef, useApi } from '@backstage/core-plugin-api';

import { GraphQLResult, getError } from './apollo';
import { Exact } from './graphql';

// Options that can be passed when using the hook
export type StandardDeleteHookOptions<
  TMutation = unknown,
  TVariables = unknown,
> = {
  onComplete?: () => void;
  onError?: 'post-alert' | ((error: Error | ApolloError) => void);
  update?: MutationHookOptions<TMutation, TVariables>['update'];
};

// The result type for the delete hook
export type StandardDeleteHookResult<TName extends string> = {
  [K in TName]: (name: string) => Promise<void>;
} & {
  error: Error | ApolloError | null;
  isDeleting: boolean;
  called: boolean;
};

// The type for the delete mutation result - includes Record<string, unknown>
// to accept generated types like GoogleProtobufEmpty that may lack __typename
export type DeleteResult =
  | {
      __typename?: string;
    }
  | {
      __typename: 'Error';
      message?: string | null;
      code: string;
    }
  | Record<string, unknown>
  | null
  | undefined;

export function withStandardDeleteHook<
  TMutation extends Partial<Record<TField, DeleteResult>>,
  TField extends string,
>(
  useMutation: (
    options?: MutationHookOptions<TMutation, Exact<{ name: string }>>,
  ) => MutationTuple<TMutation, Exact<{ name: string }>>,
  mutationField: TField,
  mutationOptions?: Pick<
    MutationHookOptions<TMutation, Exact<{ name: string }>>,
    'awaitRefetchQueries' | 'refetchQueries' | 'update'
  >,
): (
  options?: StandardDeleteHookOptions<TMutation, Exact<{ name: string }>>,
) => StandardDeleteHookResult<TField> {
  return (
    options?: StandardDeleteHookOptions<TMutation, Exact<{ name: string }>>,
  ) => {
    const alert = useApi(alertApiRef);
    const [mutate, { loading, error: apolloError, called, data }] =
      useMutation(mutationOptions);

    const error = useMemo(() => {
      if (apolloError) {
        return apolloError;
      }

      if (data) {
        const resultError = getError(
          data[mutationField] as GraphQLResult | null | undefined,
        );
        if (resultError) {
          return new Error(resultError.message || 'Unknown error');
        }
      }

      return null;
    }, [apolloError, data]);

    // we use a ref to avoid that the onError is called on every render
    // if its not stable.
    const onErrorValue = useRef(options?.onError);
    useLayoutEffect(() => {
      onErrorValue.current = options?.onError;
    }, [options?.onError]);

    useEffect(() => {
      if (error && onErrorValue.current) {
        if (onErrorValue.current === 'post-alert') {
          alert.post({
            severity: 'error',
            message: error.message,
          });
        } else {
          onErrorValue.current(error);
        }
      }
    }, [error, alert, onErrorValue]);

    // we use a ref to avoid that the onComplete is called on every render
    // if its not stable.
    const onCompleteValue = useRef(options?.onComplete);
    useLayoutEffect(() => {
      onCompleteValue.current = options?.onComplete;
    }, [options?.onComplete]);

    useEffect(() => {
      if (called && !error && onCompleteValue.current) {
        onCompleteValue.current();
      }
    }, [called, error]);

    // we use a ref to avoid recreating the delete callback when update changes
    const updateRef = useRef(options?.update);
    useLayoutEffect(() => {
      updateRef.current = options?.update;
    }, [options?.update]);

    const deleteItem = useCallback(
      async (name: string): Promise<void> => {
        const response = await mutate({
          variables: { name },
          update: updateRef.current,
        });

        const resultError = getError(
          response.data?.[mutationField] as GraphQLResult | null | undefined,
        );
        if (resultError) {
          throw new Error(resultError.message || 'Unknown error');
        }
      },
      [mutate],
    );

    return {
      [mutationField]: deleteItem,
      error,
      isDeleting: loading,
      called,
    } as StandardDeleteHookResult<TField>;
  };
}
